import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { incomeExpenseApi, ProjectBankAccount } from '../api/incomeExpenseApi';
import { getApiErrorMessage } from '../utils/apiErrors';
import ProjectBankSubLedgerPanel from './income-expense/components/ProjectBankSubLedgerPanel';

const formatCurrency = (n: number) => `${new Intl.NumberFormat('vi-VN').format(Math.round(n))}đ`;

interface TransactionReportSubLedgerTabProps {
  buildingId: string;
}

export const TransactionReportSubLedgerTab: React.FC<TransactionReportSubLedgerTabProps> = ({
  buildingId,
}) => {
  const [accounts, setAccounts] = useState<ProjectBankAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState('');

  const loadAccounts = useCallback(async () => {
    if (!buildingId) {
      setAccounts([]);
      setSelectedAccountId('');
      return;
    }
    setLoading(true);
    try {
      const rows = await incomeExpenseApi.listProjectBankAccounts(buildingId);
      setAccounts(rows);
      setSelectedAccountId((prev) => {
        if (prev && rows.some((a) => a.id === prev)) return prev;
        return rows[0]?.id ?? '';
      });
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Không thể tải tài khoản dự án'));
      setAccounts([]);
      setSelectedAccountId('');
    } finally {
      setLoading(false);
    }
  }, [buildingId]);

  useEffect(() => {
    void loadAccounts();
  }, [loadAccounts]);

  const selectedAccount = useMemo(
    () => accounts.find((a) => a.id === selectedAccountId) ?? null,
    [accounts, selectedAccountId],
  );

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Đối chiếu sao kê ngân hàng với sổ thu chi: tải file sao kê và chạy ghép tự động tại đây. Ghép thủ công / tạo
        phiếu trên từng dòng thực hiện tại{' '}
        <strong className="text-gray-800">Quản lý thu chi → Tài khoản ngân hàng → Tài khoản dự án</strong>.
      </p>

      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left w-10" aria-label="Chọn" />
              <th className="px-4 py-3 text-left">Tên tài khoản</th>
              <th className="px-4 py-3 text-left">Ngân hàng</th>
              <th className="px-4 py-3 text-right">Số dư CK</th>
              <th className="px-4 py-3 text-right">Số dư TM</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-gray-500">Đang tải...</td>
              </tr>
            ) : accounts.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-gray-500">
                  Chưa có tài khoản dự án cho tòa nhà này
                </td>
              </tr>
            ) : (
              accounts.map((account) => {
                const selected = account.id === selectedAccountId;
                return (
                  <tr
                    key={account.id}
                    className={`border-t cursor-pointer ${selected ? 'bg-cyan-50' : 'hover:bg-gray-50'}`}
                    onClick={() => setSelectedAccountId(account.id)}
                  >
                    <td className="px-4 py-3 text-center">
                      <input
                        type="radio"
                        name="subledger-account"
                        checked={selected}
                        onChange={() => setSelectedAccountId(account.id)}
                        className="text-cyan-600 focus:ring-cyan-500"
                        aria-label={`Chọn ${account.name}`}
                      />
                    </td>
                    <td className="px-4 py-3 font-medium">{account.name}</td>
                    <td className="px-4 py-3">
                      <div>{account.bankAccountNumber || '—'}</div>
                      {account.bankName ? (
                        <div className="text-xs text-gray-500 truncate max-w-xs">{account.bankName}</div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-right">{formatCurrency(account.transferBalance)}</td>
                    <td className="px-4 py-3 text-right">{formatCurrency(account.cashBalance)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {selectedAccount ? (
        <ProjectBankSubLedgerPanel
          account={selectedAccount}
          buildingId={buildingId}
          readOnlyRowActions
        />
      ) : !loading && accounts.length > 0 ? (
        <p className="text-sm text-gray-500 text-center py-6">Chọn một tài khoản để xem sổ phụ.</p>
      ) : null}
    </div>
  );
};

export default TransactionReportSubLedgerTab;
